import { consoleCopy } from '@/copy'
import type {
  AgentParticipant,
  AuthorRef,
  HistoricalMessage,
  ThreadSummary,
} from '../../types/api'

interface ParticipantView {
  /** Stable id (agent_id or username). */
  id: string
  /** Display name. */
  name: string
  isAgent: boolean
}

/**
 * Resolve a thread's typed participants to display views. Classification is
 * by `kind` (D1) — never by roster matching, so a username colliding with an
 * agent id stays human. The agents roster is used for display names only;
 * event sources are filtered out server-side.
 */
export function resolveParticipants(
  participants: AuthorRef[],
  agents: AgentParticipant[],
): ParticipantView[] {
  const agentById = new Map(agents.map((a) => [a.agent_id, a.name]))
  const out: ParticipantView[] = []
  for (const p of participants) {
    if (p.kind === 'agent') {
      out.push({
        id: p.display_id,
        name: agentById.get(p.display_id) ?? p.display_id,
        isAgent: true,
      })
    } else {
      out.push({
        id: p.display_id,
        name: p.display_id,
        isAgent: false,
      })
    }
  }
  return out
}

/** Resolve a single message author to a display view (name + isAgent).
 * Classification is by the typed `author.kind` (D1). */
export function authorView(
  msg: HistoricalMessage,
  agents: AgentParticipant[],
  currentUser: string | null,
): ParticipantView {
  const author = msg.author
  if (author.kind === 'agent') {
    const name = agents.find((a) => a.agent_id === author.display_id)?.name
    return {
      id: author.display_id,
      name: name ?? author.display_id,
      isAgent: true,
    }
  }
  // Event sources render their event type; humans render their username.
  return {
    id: author.display_id || currentUser || consoleCopy.you,
    name: author.display_id || currentUser || consoleCopy.you,
    isAgent: false,
  }
}

/**
 * Compact relative age from a millisecond timestamp ("now", "5m", "3h", "2d").
 * Pass `now` (e.g. from `useNow`) for live-refreshing callers; defaults to
 * `Date.now()` for one-shot use.
 */
function ageFromMs(ms: number, now: number = Date.now()): string {
  const s = Math.max(0, (now - ms) / 1000)
  if (s < 45) return consoleCopy.age.now
  if (s < 90) return consoleCopy.age.minutes(1)
  const m = Math.round(s / 60)
  if (m < 60) return consoleCopy.age.minutes(m)
  const h = Math.round(m / 60)
  if (h < 24) return consoleCopy.age.hours(h)
  const d = Math.round(h / 24)
  return consoleCopy.age.days(d)
}

/**
 * Age label for thread list rows. Shows relative age for recent threads ("now",
 * "5m", "3h") and a clock/date for older ones, so the user gets temporal context
 * beyond a bare day count. Same-day threads show time (e.g. "9:36 AM");
 * older threads show month/day (e.g. "Jul 30").
 *
 * Pass `now` (e.g. from `useNow`) for live-refreshing callers; defaults to
 * `Date.now()` for one-shot use.
 */
export function threadAgeDisplay(ms: number, now: number = Date.now()): string {
  const nowDate = new Date(now)
  const then = new Date(ms)
  const sameDay =
    nowDate.getFullYear() === then.getFullYear() &&
    nowDate.getMonth() === then.getMonth() &&
    nowDate.getDate() === then.getDate()
  if (sameDay) {
    // Relative for recent, clock time for anything older than an hour today.
    const s = Math.max(0, (now - ms) / 1000)
    if (s < 3600) return ageFromMs(ms, now)
    return then.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  return then.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Tri-state bucket of a thread for the redesigned board: resolved
 * (durable, manual) beats needs_you (derived, Phase 0) beats active —
 * the same precedence the server bakes into `ThreadSummary.needs_you`.
 */
export function threadBucket(
  t: ThreadSummary,
): 'needs_you' | 'active' | 'resolved' {
  if (t.status === 'resolved') return 'resolved'
  if (t.needs_you) return 'needs_you'
  return 'active'
}
